/**
 * CustomerNoticePanel
 * Reusable component for SVR and RO detail pages.
 * Shows pending notices and notice history (sent/dismissed).
 * PIC or admin can send via WhatsApp or Email, or dismiss.
 */
import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Bell, CheckCircle2, Loader2, Mail, MessageSquare, X } from "lucide-react";
import { formatDateTime } from "@/lib/dateUtils";

interface Props {
  sourceType: "svr" | "ro";
  sourceId: number;
  canSend: boolean; // true if current user is PIC or admin
}

export function CustomerNoticePanel({ sourceType, sourceId, canSend }: Props) {
  const { data: notices, refetch } = trpc.customerNotices.list.useQuery({ sourceType, sourceId });
  const sendNotice = trpc.customerNotices.send.useMutation({
    onSuccess: (data) => {
      if (data.delivered === false && data.suppressed) {
        toast.warning(data.suppressed.staffMessage);
      } else {
        toast.success("Notification sent to customer");
      }
      setConfirming(null);
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });
  const dismissNotice = trpc.customerNotices.dismiss.useMutation({
    onSuccess: () => {
      toast.success("Notice dismissed");
      refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  const [confirming, setConfirming] = useState<{ noticeId: number; channel: "whatsapp" | "email" } | null>(null);

  if (!notices || notices.length === 0) return null;

  const pending = notices.filter((n) => !n.sentAt && !n.dismissedAt);
  const history = notices.filter((n) => n.sentAt || n.dismissedAt);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-2">
          <Bell className="h-3.5 w-3.5" />
          Customer Notifications
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Pending notices */}
        {pending.length > 0 && (
          <div className="space-y-3">
            {pending.map((notice) => (
              <div key={notice.id} className={`border rounded-md p-3 space-y-2 ${
                notice.statusAtCreate === "invoice_ready"
                  ? "border-emerald-200 bg-emerald-50"
                  : "border-amber-200 bg-amber-50"
              }`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className={`text-xs font-semibold ${
                      notice.statusAtCreate === "invoice_ready" ? "text-emerald-900" : "text-amber-900"
                    }`}>
                      {notice.statusAtCreate === "invoice_ready" ? "Pending: Send invoice to customer" : "Pending: Notify customer"}
                    </p>
                    <p className={`text-xs mt-0.5 ${
                      notice.statusAtCreate === "invoice_ready" ? "text-emerald-700" : "text-amber-700"
                    }`}>
                      {notice.statusAtCreate === "invoice_ready"
                        ? "Invoice PDF has been uploaded by Finance"
                        : <>Status update: <span className="font-medium">{notice.statusLabel ?? notice.statusAtCreate}</span></>}
                    </p>
                    {notice.customerName && (
                      <p className="text-xs text-amber-600">Customer: {notice.customerName}</p>
                    )}
                  </div>
                  {canSend && (
                    <button
                      className="text-amber-400 hover:text-amber-600 p-0.5"
                      title="Dismiss this notice"
                      onClick={() => dismissNotice.mutate({ noticeId: notice.id })}
                      disabled={dismissNotice.isPending}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>

                {canSend && (
                  <>
                    {confirming?.noticeId === notice.id ? (
                      <div className="bg-white border border-amber-200 rounded p-2 space-y-2">
                        <p className="text-xs text-amber-800">
                          Send status update via {confirming.channel === "whatsapp" ? "WhatsApp" : "email"}?
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 h-7 text-xs"
                            onClick={() => setConfirming(null)}
                            disabled={sendNotice.isPending}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            className="flex-1 h-7 text-xs bg-black text-white hover:bg-gray-800"
                            onClick={() => sendNotice.mutate({ noticeId: notice.id, channel: confirming.channel })}
                            disabled={sendNotice.isPending}
                          >
                            {sendNotice.isPending ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              "Confirm Send"
                            )}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 h-7 text-xs gap-1"
                          onClick={() => setConfirming({ noticeId: notice.id, channel: "whatsapp" })}
                        >
                          <MessageSquare className="h-3 w-3" />
                          WhatsApp
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 h-7 text-xs gap-1"
                          onClick={() => setConfirming({ noticeId: notice.id, channel: "email" })}
                        >
                          <Mail className="h-3 w-3" />
                          Email
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Notice history */}
        {history.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Notification History</p>
            {history.map((notice) => (
              <div key={notice.id} className="flex gap-2.5 text-xs">
                {notice.sentAt ? (
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" />
                ) : (
                  <X className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
                )}
                <div>
                  {notice.sentAt ? (
                    <>
                      <span className="font-medium">{notice.sentBy ?? "Staff"}</span>
                      <span className="text-muted-foreground ml-1">
                        sent via {notice.channel === "whatsapp" ? "WhatsApp" : "email"} —{" "}
                        <span className="italic">{notice.statusLabel ?? notice.statusAtCreate}</span>
                      </span>
                      <span className="text-muted-foreground ml-1">{formatDateTime(notice.sentAt)}</span>
                    </>
                  ) : (
                    <>
                      <span className="font-medium">{notice.dismissedBy ?? "Staff"}</span>
                      <span className="text-muted-foreground ml-1">
                        dismissed notice —{" "}
                        <span className="italic">{notice.statusLabel ?? notice.statusAtCreate}</span>
                      </span>
                      <span className="text-muted-foreground ml-1">{formatDateTime(notice.dismissedAt!)}</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
